import { type ThunkAction } from "redux-thunk";
import { type AnyAction } from "~/app/store/types";
import { type PriceInfo } from "~/domains/pricing/api/PriceInfo";
import { type ProductData, ProductType } from "~/domains/product/types/ProductData";
import { type AvailabilityInfo } from "~/domains/product/api/pna/AvailabilityInfo";
import { productsActions } from "~/domains/product/redux/pnaReducers";
import { type RootState } from "~/app/store";
import { getLinearPriceInfo } from "~/domains/linear/api/GetLinearPriceInfoSingle";
import { getCacheKey, selectProductData, selectProductDescriptor } from "~/domains/product/redux/selectors";
import { fetchProductInfo } from "~/domains/product/api/fetchProductInfo";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import { getHybrisProductCode } from "~/utils/cartHelper";
import { requestAvailabilityDataSingle } from "~/domains/product/api/availabilityService";
import { requestPriceDataSingle } from "~/domains/pricing/api/priceService";
import { getLinearAvailabilityInfo } from "~/domains/linear/api/GetLinearAvailabilityInfoSingle";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";

type ThunkResult<R> = ThunkAction<R, RootState, undefined, AnyAction>;

export const ADD_PRODUCT_BASIC = "products: add basic product info";

// Availability calls in progress
const aPromisesInFlight: { [code: string]: Promise<AvailabilityInfo> } = {};
// Price calls in progress
const pPromisesInFlight: { [code: string]: Promise<PriceInfo[]> } = {};

export function addBaseProductDataAction(products: ProductData | ProductData[], override: boolean = false) {
  return {
    type: ADD_PRODUCT_BASIC,
    payload: {
      products: Array.isArray(products) ? products : [products],
    },
  } as const;
}

/**
 *
 * @param productOrCode - code of the product to load
 * @param priceReference -
 * @param retryIfError - try loading again if the previous attempt failed
 */
export function requestPriceData(
  productOrCode: ProductData | string,
  priceReference: string,
  retryIfError?: boolean,
  packCode?: string,
  forceRefresh?: boolean,
): ThunkResult<Promise<PriceInfo[]>> {
  return (dispatch, getState) => {
    const state = getState();
    const product = typeof productOrCode === "string" ? selectProductData(productOrCode)(state) : productOrCode;
    const cacheKey = getCacheKey({ productCode: product.code, priceReference: priceReference });

    const entry = state.products.price[cacheKey];

    if (!userHasPermission(Permission.PRICE)) {
      if (!entry) {
        const data: PriceInfo[] = [];
        dispatch(productsActions.price.success(cacheKey, []));
        return Promise.resolve(data);
      }
      return Promise.resolve(entry.data!);
    }

    if (entry && (!forceRefresh || entry.state === "loading")) {
      const promiseInFlight: Promise<PriceInfo[]> | undefined = pPromisesInFlight[cacheKey];
      if (entry.data) {
        return Promise.resolve(entry.data);
      } else if (entry.state === "error" && !retryIfError) {
        return Promise.reject(new Error("error"));
      } else if (entry.state === "loading" || promiseInFlight) {
        return promiseInFlight;
      }
    }

    const promise = dispatch(fetchPriceAction(product, priceReference, packCode));
    pPromisesInFlight[cacheKey] = promise;
    return promise;
  };
}

function fetchPriceAction(
  product: ProductData,
  priceReference: string,
  packCode?: string,
): ThunkResult<Promise<PriceInfo[]>> {
  return async (dispatch) => {
    // We use pnaCode to fetch, but it should be cached under code - so that the fake product-bundle can find the data
    const cacheKey = getCacheKey({ productCode: product.code, priceReference: priceReference });

    dispatch(productsActions.price.load(cacheKey));

    let promise;
    if (product.type === ProductType.CONFIGURATOR) {
      promise = getLinearPriceInfo(getHybrisProductCode(product.code), product.configId, priceReference);
    } else {
      promise = requestPriceDataSingle({
        productCode: product.pnaCode ?? product.code,
        priceReference,
        packCode,
      });
    }

    promise
      .then((info) => {
        dispatch(productsActions.price.success(cacheKey, info));
        return info;
      })
      .catch((err) => {
        dispatch(productsActions.price.error(cacheKey));
        throw err;
      });

    promise.finally(() => delete pPromisesInFlight[cacheKey]).catch(() => delete pPromisesInFlight[cacheKey]);

    return promise;
  };
}

/**
 *
 * @param productOrCode - code of the product to load
 * @param retryIfError - try loading again if the previous attempt failed
 */
export function requestAvailabilityData(
  productOrCode: ProductData | string,
  retryIfError?: boolean,
  packCode?: string,
  forceRefresh?: boolean,
): ThunkResult<Promise<AvailabilityInfo | undefined>> {
  return (dispatch, getState) => {
    const state = getState();
    const product = typeof productOrCode === "string" ? selectProductData(productOrCode)(state) : productOrCode;
    const cacheKey = getCacheKey({ productCode: product.code, priceReference: "" });

    const entry = getState().products.availability[cacheKey];

    if (!userHasPermission(Permission.AVAILABILITY)) {
      if (!entry) {
        dispatch(productsActions.availability.success(cacheKey, undefined));
        return Promise.resolve(undefined);
      }
      return Promise.resolve(entry.data!);
    }

    if (entry && (!forceRefresh || entry.state === "loading")) {
      const promiseInFlight: Promise<AvailabilityInfo> | undefined = aPromisesInFlight[cacheKey];
      if (entry.data) {
        return Promise.resolve(entry.data);
      } else if (entry.state === "error" && !retryIfError) {
        return Promise.reject(new Error("error"));
      } else if (entry.state === "loading" || promiseInFlight) {
        return promiseInFlight;
      }
    }

    const promise = dispatch(fetchAvailabilityAction(product, packCode));
    aPromisesInFlight[cacheKey] = promise;
    return promise;
  };
}

function fetchAvailabilityAction(product: ProductData, packCode?: string): ThunkResult<Promise<AvailabilityInfo>> {
  return (dispatch) => {
    // We use pnaCode to fetch, but it should be cached under code - so that the fake product-bundle can find the data
    const cacheKey = getCacheKey({ productCode: product.code, priceReference: "" });

    dispatch(productsActions.availability.load(cacheKey));

    let promise;
    if (product.type === ProductType.CONFIGURATOR) {
      promise = getLinearAvailabilityInfo(product.pnaCode, product.configId);
    } else {
      promise = requestAvailabilityDataSingle({ productCode: product.pnaCode ?? product.code, packCode });
    }

    promise
      .then((info) => {
        dispatch(productsActions.availability.success(cacheKey, info));
        return info;
      })
      .catch((err) => {
        dispatch(productsActions.availability.error(cacheKey));
        throw err;
      });

    promise.finally(() => delete aPromisesInFlight[cacheKey]).catch(() => delete aPromisesInFlight[cacheKey]);
    return promise;
  };
}

interface RequestFullProductDataOptions {
  forceFetchBasicData?: boolean;
  fetchPrice?: boolean;
  fetchAvailability?: boolean;
}

export function requestFullProductData(
  code: string,
  priceReference: string,
  options: RequestFullProductDataOptions = {},
): ThunkResult<Promise<ProductDescriptor>> {
  return async (dispatch, getState) => {
    const state = getState();

    priceReference = priceReference === "-" ? "" : priceReference;

    let product = state.products.basic[code];
    if (!product || (options.forceFetchBasicData && !product?.isFullProduct)) {
      product = await fetchProductInfo(code);
      dispatch(addBaseProductDataAction(product, options.forceFetchBasicData));
    }

    const fetchPrice = options.fetchPrice ?? true;
    const fetchAvailability = options.fetchAvailability ?? true;

    const pricePromise = fetchPrice ? dispatch(requestPriceData(code, priceReference, true)) : Promise.resolve([]);
    const availabilityPromise = fetchAvailability
      ? dispatch(requestAvailabilityData(code, true))
      : Promise.resolve(undefined);

    await Promise.all([pricePromise, availabilityPromise]);

    return selectProductDescriptor(getState(), { productCode: code, priceReference });
  };
}

export function requestBaseProductData(
  code: string,
  options: RequestFullProductDataOptions = {},
): ThunkResult<Promise<ProductData>> {
  return async (dispatch) => {
    const product = await fetchProductInfo(code);
    dispatch(addBaseProductDataAction(product, options.forceFetchBasicData));

    return product;
  };
}
